<script setup>
import { Link } from '@inertiajs/vue3'
import ClickToActionSection from '@/components/ClickToActionSection.vue'
import { WrenchScrewdriverIcon, CalendarDaysIcon } from '@heroicons/vue/24/solid'
import { index as contactIndex } from '@/actions/App/Http/Controllers/ContactController'
import ServiceCard from '@/pages/services/Partials/ServiceCard.vue'

const seo = {
    title: 'Servis / Sezonní údržba',
    description:
        'Servis a sezonní údržba topných systémů a kotlů. Diagnostika závad, opravy, sezónní kontroly plynových kotlů, elektrokotlů a plynových topidel. Spolehlivý a bezpečný provoz vašeho zařízení.',
}

const services = [
    {
        name: 'Servis a opravy',
        description:
            'Zajišťujeme servis a opravy topných systémů a kotlů všech běžných typů. Zaměřujeme se na diagnostiku závad a jejich rychlé a spolehlivé odstranění.',
        icon: WrenchScrewdriverIcon,
        features: [
            'Diagnostiku poruch a zjištění příčiny problému',
            'Opravy kotlů a jednotlivých komponent',
            'Řešení nefunkčního nebo nestabilního chodu zařízení',
            'Opravy netěsností a závad na rozvodech',
            'Kontrolu a obnovení správné funkce systému',
            'Další související servisní úkony dle typu a stavu zařízení',
        ],
        footer: 'Cílem servisního zásahu je co nejrychleji obnovit plnou funkčnost zařízení a zajistit jeho bezpečný provoz.',
    },
    {
        name: 'Sezónní kontroly topných zařízení',
        description:
            'Provádíme odborné sezónní kontroly plynových kotlů, elektrokotlů a plynových topidel. Pravidelná údržba je klíčová pro bezpečný, úsporný a spolehlivý provoz vašeho zařízení.',
        icon: CalendarDaysIcon,
        features: [
            'Kontrola celkové funkčnosti kotle',
            'Kontrola těsnosti plynových rozvodů a spojů',
            'Kontrola spalování a správného chodu zařízení',
            'Měření emisí',
            'Seřízení a kontrola výkonu kotle',
            'Čištění filtrů, výměníků, elektrod a hořáků',
            'Odhalení případných závad a doporučení jejich řešení',
            'Další související úkony dle typu zařízení',
        ],
        footer: 'Díky pravidelné kontrole předejdete poruchám, prodloužíte životnost zařízení a zajistíte jeho bezpečný a efektivní provoz.',
    },
]

const benefits = [
    { value: '24h', label: 'Havarijní služba' },
    { value: '30+', label: 'Let zkušeností' },
    { value: '100%', label: 'Záruka na práci' },
]
</script>

<template>
    <Head :title="seo.title">
        <meta
            name="description"
            :content="seo.description"
        />
    </Head>

    <section>
        <!-- Hero Section -->
        <div class="relative isolate overflow-hidden bg-gray-900">
            <img
                src="/images/kalianko-team.png"
                alt="Servis a sezonní údržba - profesionální servisní služby"
                class="absolute inset-0 -z-10 size-full object-cover opacity-20"
            />
            <div class="absolute inset-0 -z-10 bg-linear-to-b from-gray-900/50 via-gray-900/80 to-gray-900" />

            <div class="mx-auto max-w-4xl px-6 py-32 text-center sm:py-40 lg:px-8 lg:py-56">
                <h1 class="text-4xl font-bold tracking-tight text-white sm:text-6xl">Servis / Sezonní údržba</h1>

                <p class="mx-auto mt-6 max-w-2xl text-lg/8 text-gray-300">
                    Spolehlivý servis a pravidelná údržba topných systémů a kotlů. Diagnostika závad, opravy a sezónní
                    kontroly pro bezpečný a úsporný provoz vašeho zařízení.
                </p>

                <div class="mt-10 flex flex-col items-center justify-center gap-2 lg:flex-row">
                    <Link
                        :href="contactIndex().url + '#demand-form'"
                        class="primary-link inline-flex w-full items-center justify-center rounded-full text-lg lg:w-fit"
                    >
                        Nezávazná poptávka
                    </Link>
                </div>

                <!-- Quick benefits -->
                <div class="mt-16 grid grid-cols-3 gap-4 border-t border-white/10 pt-10">
                    <div
                        v-for="benefit in benefits"
                        :key="benefit.label"
                        class="text-center"
                    >
                        <div class="text-brand-primary text-3xl font-bold">{{ benefit.value }}</div>
                        <div class="mt-1 text-sm text-gray-400">{{ benefit.label }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Features Section -->
        <div class="section-width space-y-20 px-2 py-24 sm:py-32 lg:px-0">
            <div class="mx-auto max-w-2xl text-center">
                <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Naše služby</p>
                <h2 class="heading-2 mt-2">Co pro vás můžeme udělat</h2>
                <p class="paragraph mt-4">
                    Zajišťujeme kompletní servis a pravidelnou údržbu topných systémů a kotlů. Každý zásah provádíme
                    odborně, s důrazem na bezpečnost, spolehlivost a dlouhou životnost vašeho zařízení.
                </p>
            </div>

            <div class="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2">
                <ServiceCard
                    v-for="service in services"
                    :key="service.name"
                    :service="service"
                />
            </div>

            <ClickToActionSection />
        </div>
    </section>
</template>
